"use client";

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Users, UserCheck, Lock } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listVersionAccess, createVersionAccess, deleteVersionAccess } from "@/lib/api/versions";
import { listUsers } from "@/lib/api/users";
import { getErrorMessage } from "@/lib/api/client";
import type { SimulatorVersionAccess, UserRole } from "@/types";

interface AccessTabProps {
  versionId: number;
  isEditable: boolean;
}

const roleLabels: Record<UserRole, string> = {
  viewer: "Consultant",
  user: "Utilisateur",
  manager: "Responsable",
  admin: "Administrateur",
};

export function AccessTab({ versionId, isEditable }: AccessTabProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accessType, setAccessType] = useState<"role" | "user">("role");
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const { data: accessRules, isLoading } = useQuery({
    queryKey: ["version-access", versionId],
    queryFn: () => listVersionAccess(versionId),
  });

  const { data: users } = useQuery({
    queryKey: ["users", { pageSize: 100 }],
    queryFn: () => listUsers({ pageSize: 100 }),
    enabled: dialogOpen,
  });

  const invalidateAccess = () => {
    queryClient.invalidateQueries({ queryKey: ["version-access", versionId] });
  };

  const createMutation = useMutation({
    mutationFn: (data: { role?: string; user_id?: number }) =>
      createVersionAccess(versionId, data),
    onSuccess: () => {
      invalidateAccess();
      toast.success("Règle d'accès ajoutée");
      closeDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (accessId: number) => deleteVersionAccess(versionId, accessId),
    onSuccess: () => {
      invalidateAccess();
      toast.success("Règle d'accès supprimée");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setAccessType("role");
    setSelectedRole("");
    setSelectedUserId("");
  };

  const handleSubmit = () => {
    if (accessType === "role" && selectedRole) {
      createMutation.mutate({ role: selectedRole });
    } else if (accessType === "user" && selectedUserId) {
      createMutation.mutate({ user_id: parseInt(selectedUserId) });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const roleRules = accessRules?.filter((r) => r.role) || [];
  const userRules = accessRules?.filter((r) => r.user_id) || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Contrôle d&apos;accès</CardTitle>
          <CardDescription>
            Gérez qui peut utiliser cette version du simulateur
          </CardDescription>
        </div>
        {isEditable && (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Ajouter une règle
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {accessRules?.length === 0 ? (
          <div className="text-center py-12">
            <Lock className="h-12 w-12 mx-auto mb-4 text-success opacity-50" />
            <p className="text-muted-foreground">
              Aucune restriction d&apos;accès
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Cette version est accessible à tous les utilisateurs authentifiés
            </p>
            {isEditable && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setDialogOpen(true)}
              >
                Restreindre l&apos;accès
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Role-based rules */}
            {roleRules.length > 0 && (
              <div>
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
                  <Users className="h-4 w-4" />
                  Accès par rôle
                </h4>
                <div className="space-y-2">
                  {roleRules.map((rule) => (
                    <div
                      key={rule.id}
                      className="flex items-center justify-between p-4 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-laposte-navy/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-laposte-navy" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {roleLabels[rule.role as UserRole] || rule.role}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Tous les utilisateurs avec ce rôle
                          </p>
                        </div>
                      </div>
                      {isEditable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteMutation.mutate(rule.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User-specific rules */}
            {userRules.length > 0 && (
              <div>
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
                  <UserCheck className="h-4 w-4" />
                  Accès utilisateurs spécifiques
                </h4>
                <div className="space-y-2">
                  {userRules.map((rule) => (
                    <div
                      key={rule.id}
                      className="flex items-center justify-between p-4 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-info/10 flex items-center justify-center">
                          <UserCheck className="h-5 w-5 text-info" />
                        </div>
                        <div>
                          <p className="font-medium">
                            Utilisateur #{rule.user_id}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Accès individuel
                          </p>
                        </div>
                      </div>
                      {isEditable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteMutation.mutate(rule.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4 rounded-lg bg-muted/50 text-sm text-muted-foreground">
              <p>
                <strong>Note:</strong> Les administrateurs et managers ont toujours accès
                à toutes les versions. Les règles ci-dessus restreignent uniquement
                l&apos;accès pour les autres utilisateurs.
              </p>
            </div>
          </div>
        )}
      </CardContent>

      {/* Add Access Dialog */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une règle d&apos;accès</DialogTitle>
            <DialogDescription>
              Définissez qui peut accéder à cette version
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type d&apos;accès</Label>
              <Select
                value={accessType}
                onValueChange={(value) => setAccessType(value as "role" | "user")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">Par rôle</SelectItem>
                  <SelectItem value="user">Utilisateur spécifique</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {accessType === "role" && (
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).map(([role, label]) => (
                      <SelectItem key={role} value={role}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {accessType === "user" && (
              <div className="space-y-2">
                <Label>Utilisateur</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un utilisateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {users?.items.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.full_name || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                createMutation.isPending ||
                (accessType === "role" && !selectedRole) ||
                (accessType === "user" && !selectedUserId)
              }
            >
              {createMutation.isPending ? "Ajout..." : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

